import React, { useState, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import * as S from './style'
import { OptionsButton, SubHeaderContainer, LeftSide, RightSide, ButtonCreate, RefreshIcon, RefreshIconContainer, FilterWrapper } from '../../../components/styles/subheader-style'
import { Plus, Tag, Ellipsis, Filter as FilterIcon } from 'lucide-react'
import refresh_act from '../../../assets/refresh-active.svg'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu'
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay'
import { Header, Filter } from '../../../components/SubHeaderFilter/types'

export const collectionHeaders: Header[] = [
  { key: 'name', label: 'Coleção', type: 'string' },
  { key: 'collectionColor', label: 'Cor', type: 'string' },
  { key: 'families', label: 'Família', type: 'string' },
  { key: 'grades', label: 'Grade', type: 'string' },
  { key: 'groups', label: 'Grupo', type: 'string' },
  { key: 'themes', label: 'Tema', type: 'string' },
  { key: 'fabrics', label: 'Tecido', type: 'string' },
  { key: 'trimmings', label: 'Aviamento', type: 'string' },
  { key: 'episodes', label: 'Episódio', type: 'string' }
]

export interface CollectionsOutletType {
  refresh: number
  search: string
  Styled: typeof S
  isLoading?: boolean
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>
  appliedFilters: Filter[]
}

const Collections: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [search, setSearch] = useState<string>('')
  const [refresh, setRefresh] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [showOptions, setShowOptions] = useState<boolean>(false)
  const optionsRef = useRef<HTMLUListElement | null>(null)
  const optionsButtonRef = useRef<HTMLDivElement | null>(null)
  const [optionsPosition, setOptionsPosition] = useState<{ top: number; right: number } | null>(null)
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const filterButtonRef = useRef<HTMLDivElement | null>(null)

  const context = useOutletContext<any>()
  const collectionsFiltersState = context?.collectionsFiltersState || usePersistedFilters('collections')

  const handleNewCollection = () => {
    window.location.href = '/panel/collection/register'
  }

  const toggleOptions = () => {
    setShowOptions((previous) => {
      if (previous) {
        return false
      }

      if (optionsButtonRef.current) {
        const buttonRect = optionsButtonRef.current.getBoundingClientRect()
        const top = buttonRect.bottom + 8
        const right = Math.max(16, window.innerWidth - buttonRect.right)
        setOptionsPosition({ top, right })
      }

      return true
    })
  }

  const outletContext: CollectionsOutletType = {
    refresh,
    search,
    Styled: S,
    isLoading,
    setIsLoading,
    appliedFilters: collectionsFiltersState.appliedFilters
  }

  const isRegisterOrEdit =
    location.pathname.includes('/register') ||
    location.pathname.includes('/edit')

  const isInsideProductDevelopment = location.pathname.startsWith('/panel/collection') || location.pathname.startsWith('/panel/product-development')

  useEffect(() => {
    const handleProductDevelopmentRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string }>).detail
      if (detail?.tab === 'collection') {
        setRefresh((previous) => previous + 1)
      }
    }

    const handleProductDevelopmentCreate = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string }>).detail
      if (detail?.tab === 'collection') {
        navigate('/panel/collection/register')
      }
    }

    window.addEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
    window.addEventListener('product-development-create', handleProductDevelopmentCreate as EventListener)

    return () => {
      window.removeEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
      window.removeEventListener('product-development-create', handleProductDevelopmentCreate as EventListener)
    }
  }, [navigate])

  useEffect(() => {
    const handleClickOutsideOptions = (event: MouseEvent) => {
      const target = event.target as Node
      if (optionsRef.current && optionsRef.current.contains(target)) {
        return
      }
      if (optionsButtonRef.current && optionsButtonRef.current.contains(target)) {
        return
      }
      if (showOptions) {
        setShowOptions(false)
      }
    }

    const handleLayoutChange = () => {
      if (showOptions) {
        setShowOptions(false)
      }
    }

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutsideOptions)
      window.addEventListener('scroll', handleLayoutChange, true)
      window.addEventListener('resize', handleLayoutChange)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideOptions)
      window.removeEventListener('scroll', handleLayoutChange, true)
      window.removeEventListener('resize', handleLayoutChange)
    }
  }, [showOptions])

  if (isRegisterOrEdit) {
    return <Outlet context={outletContext} />
  }

  return (
    <>
      {!isInsideProductDevelopment && (
        <SubHeaderContainer>
          <LeftSide>
            <S.Title>Coleções</S.Title>
          </LeftSide>
          <RightSide style={{ position: 'relative', alignItems: 'center' }}>
            <RefreshIconContainer onClick={() => setRefresh(prev => prev + 1)} title="Atualizar">
              <RefreshIcon src={refresh_act} alt="Atualizar" $spinning={isLoading} />
            </RefreshIconContainer>
            <FilterWrapper>
              <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
                <FilterIcon size={18} />
              </OptionsButton>
              {isFilterMenuOpen && (
                <FilterMenu
                  headers={collectionHeaders}
                  onApplyFilter={(f) => {
                    collectionsFiltersState.handleApplyFilter(f)
                    setIsFilterMenuOpen(false)
                  }}
                  onClose={() => setIsFilterMenuOpen(false)}
                  anchorRef={filterButtonRef}
                  filters={collectionsFiltersState.appliedFilters}
                />
              )}
            </FilterWrapper>
            <OptionsButton ref={optionsButtonRef} onClick={toggleOptions} style={{ position: 'relative' }}>
              <Ellipsis size={18} />
            </OptionsButton>
            <ButtonCreate onClick={handleNewCollection}>
              <Plus size={18} />
              Nova coleção
            </ButtonCreate>
          </RightSide>
        </SubHeaderContainer>
      )}

      {!isInsideProductDevelopment && collectionsFiltersState.appliedFilters.length > 0 && (
        <AppliedFiltersDisplay
          filters={collectionsFiltersState.appliedFilters}
          onRemoveFilter={collectionsFiltersState.handleRemoveFilter}
          onClearAll={collectionsFiltersState.handleClearAll}
        />
      )}

      <S.Content>
        <Outlet context={outletContext} />
      </S.Content>
    </>
  )
}

export default Collections
